import get from "lodash/get";

/**
 * Performs a shallow equality check between two values.
 * For objects and arrays, only compares the first level of properties/elements.
 *
 * @param objA - First value to compare
 * @param objB - Second value to compare
 * @returns true if values are shallowly equal, false otherwise
 */
export function shallowEqual(objA: unknown, objB: unknown): boolean {
  // Same reference or both are the same primitive value
  if (Object.is(objA, objB)) {
    return true;
  }

  // If either is not an object or is null, they're not equal
  if (typeof objA !== "object" || objA === null || typeof objB !== "object" || objB === null) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(objA) && Array.isArray(objB)) {
    if (objA.length !== objB.length) {
      return false;
    }
    for (let i = 0; i < objA.length; i++) {
      if (!Object.is(objA[i], objB[i])) {
        return false;
      }
    }
    return true;
  }

  // If one is an array and the other isn't, they're not equal
  if (Array.isArray(objA) || Array.isArray(objB)) {
    return false;
  }

  // Get keys from both objects
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  // Different number of keys means not equal
  if (keysA.length !== keysB.length) {
    return false;
  }

  // Check if all keys and values match
  for (let i = 0; i < keysA.length; i++) {
    const key = keysA[i];

    if (!Object.prototype.hasOwnProperty.call(objB, key) || !Object.is(get(objA, key), get(objB, key))) {
      return false;
    }
  }

  return true;
}
